import React, { useEffect, useMemo, useRef, useState } from "react";

import { TimeUnit } from "chart.js/types/adapters";
import { ChartData, ChartOptions, ChartType, TooltipItem, Chart as TypeChart } from "chart.js/types/index.esm";
import Zoom from "chartjs-plugin-zoom";
import moment, { Moment } from "moment";
import { Chart, Line } from "react-chartjs-2";
import { TFunction, useTranslation } from "react-i18next";

import { Spin } from "antd";

import { ResolutionType } from "../../../../store/common_types";
import {
  CompareType,
  ConversionChartItem,
  ConversionSearchResultChartItem,
  MetricType,
} from "../../../../store/conversions/types";
import { minRangeWindowMap, resolutionUnitMap, useRangeLimit } from "../../../../utils/resolution";

import { handleSummaryResultsChartData, handleSummaryResultsChartOption } from "./search_results";

export interface ChartProps {
  items: ConversionChartItem[];
  searchResultsItems: ConversionSearchResultChartItem[];
  metric: MetricType | "search_results";
  compare: CompareType;
  resolution: ResolutionType;
  rangeFrom: Moment;
  rangeTo: Moment;
  loading: boolean;
}

const conversionsColor = [
  { key: "value", backgroundColor: "rgb(255, 99, 132)", borderColor: "rgb(255, 99, 132, 0.2)" },
  { key: "compare", backgroundColor: "rgb(54, 162, 235)", borderColor: "rgba(54, 162, 235, 0.2)" },
];

const unitMap: { [K in CompareType]: TimeUnit } = {
  "1d": "day",
  "1w": "week",
  "1m": "month",
  "1y": "year",
};

const handleChartData = (
  t: TFunction<"conversions_page">,
  items: ConversionChartItem[],
  metric: MetricType,
  compare: CompareType
): ChartData<"line", ConversionChartItem[]> => ({
  datasets: conversionsColor.map(({ key, backgroundColor, borderColor }) => ({
    label: t(`chart.${key}.${metric}`, { offset: t(`chart.offset.${compare}`) }),
    parsing: { yAxisKey: key, xAxisKey: "timestamp" },
    fill: false,
    data: items,
    cubicInterpolationMode: "monotone",
    tension: 0.4,
    backgroundColor,
    borderColor,
  })),
});

const handleChartOption = (
  t: TFunction<"conversions_page">,
  min: number,
  max: number,
  minRange: number,
  resolution: ResolutionType,
  metric: MetricType,
  compare: CompareType
): ChartOptions<"line"> => ({
  spanGaps: false,
  responsive: true,
  plugins: {
    datalabels: { display: false },
    legend: { position: "bottom" },
    tooltip: {
      position: "nearest",
      callbacks: {
        title: () => "",
        label: (tooltipItem: TooltipItem<"line">): string | string[] => {
          if (tooltipItem.datasetIndex === 0) {
            return `${t(`chart.value.${metric}`)} (${tooltipItem.label}): ${tooltipItem.formattedValue}`;
          }
          const fmt = resolutionUnitMap[resolution].tooltipFormat;
          const offset = moment(tooltipItem.label).subtract(1, unitMap[compare]).format(fmt);
          return `${t(`chart.value.${metric}`)} (${offset}): ${tooltipItem.formattedValue}`;
        },
      },
    },
    zoom: {
      limits: { x: { min, max, minRange } },
      zoom: { enabled: minRange < max - min, mode: "x", threshold: 1 },
    },
  },
  interaction: { intersect: false, mode: "index" },
  scales: {
    y: { beginAtZero: true, title: { display: true, text: t(`chart.legends.${metric}`).split("\n") } },
    x: {
      type: "time",
      time: {
        isoWeekday: 1,
        displayFormats: { minute: "YYYY-MM-DD HH:mm", hour: "YYYY-MM-DD HH:mm", day: "YYYY-MM-DD", month: "YYYY-MM" },
        ...resolutionUnitMap[resolution],
      },
      offset: false,
      ticks: { maxRotation: 0, align: "center" },
      min,
      max,
    },
  },
});

type ChartItemsType = ConversionChartItem | ConversionSearchResultChartItem;

export const ConversionsChart: React.FC<ChartProps> = (props) => {
  const { t } = useTranslation("conversions_page");

  const limit = useRangeLimit(props.rangeFrom, props.rangeTo, props.resolution);
  const [data, setData] = useState<ChartData<"line", ChartItemsType[]>>({ datasets: [] });
  const [options, setOptions] = useState<ChartOptions<"line">>({});
  const conversionsChart = useRef<TypeChart<"line", ChartItemsType[]>>();

  useEffect(() => {
    Chart.register(Zoom);
  }, []);

  useEffect(() => {
    switch (props.metric) {
      case "search_results":
        setData(handleSummaryResultsChartData(t, props.searchResultsItems));
        break;
      default:
        setData(handleChartData(t, props.items, props.metric, props.compare));
    }
  }, [props.compare, props.items, props.metric, props.searchResultsItems, t]);

  useEffect(() => {
    const minRange = minRangeWindowMap[props.resolution];
    switch (props.metric) {
      case "search_results":
        setOptions(handleSummaryResultsChartOption(t, limit.min, limit.max, minRange, props.resolution));
        break;
      default:
        setOptions(handleChartOption(t, limit.min, limit.max, minRange, props.resolution, props.metric, props.compare));
    }
  }, [t, limit, props.compare, props.metric, props.resolution]);

  useEffect(() => {
    const chart = conversionsChart.current;
    if (chart !== undefined) {
      chart.options = options;
      chart.data = data;
      chart.update("none");
    }
  }, [data, options]);

  const lineChart = useMemo(() => {
    return <Line ref={conversionsChart} data={{ datasets: [] }} height={75} type={"line" as ChartType} />;
  }, []);

  return <Spin spinning={props.loading}>{lineChart}</Spin>;
};
